// frontend/src/utils/engagementPages.js
//
// The pages the anonymous page-engagement tracker (utils/engagementBeacon.js)
// measures: public MARKETING pages only - the homepage, the blog, the two
// product landing pages and every profession landing page. Never an auth or
// account page (sign-up, login, email verification, password reset,
// dashboard, settings, admin, payment, any token path) and never a legal page:
// on a low-traffic site even an hour-truncated row for the sign-up page could
// be lined up with the one account created in that hour.
//
// MIRRORED IN THE BACKEND as ENGAGEMENT_PAGES (services/analytics_rollup.py).
// The ingest endpoint silently drops a snapshot of any other page, and
// backend/tests/api/v1/test_page_engagement.py parses this list and asserts
// the two are equal - a page goes into BOTH lists in the same change. A new
// profession page (content/professions/registry.js) must be added here too;
// the backend test fails until it is.
//
// The values are the backend's page BUCKETS: every blog article is the one
// bucket "/blog/*", matched by `engagementPageBucket` below.

/** Every measured page bucket. Order is irrelevant. */
export const ENGAGEMENT_PAGES = Object.freeze([
  "/",
  "/blog",
  "/blog/*",
  "/business-website",
  "/facebook-group-scanning",
  "/lawyers",
  "/accountants",
  "/insurance-agents",
  "/digital-marketers",
  "/dentists",
  "/dog-trainers",
  "/web-developers",
]);

const PAGE_SET = new Set(ENGAGEMENT_PAGES);

// A blog article: anything under "/blog/" (the backend's bucket_path rule).
const BLOG_ARTICLE_PREFIX = "/blog/";

/**
 * A pathname with any trailing slash removed (the bare root stays "/"), or
 * null for anything that is not a string.
 *
 * @param {unknown} path
 * @returns {string|null}
 */
export function normalizeEngagementPath(path) {
  if (typeof path !== "string") return null;
  const trimmed = path.replace(/\/+$/, "");
  return trimmed === "" && path !== "" ? "/" : trimmed;
}

/**
 * The measured page bucket of `path`, or null when the page is not measured.
 *
 * @param {unknown} path a pathname (no query or hash)
 * @returns {string|null}
 */
export function engagementPageBucket(path) {
  const normalized = normalizeEngagementPath(path);
  if (!normalized) return null;
  if (normalized.startsWith(BLOG_ARTICLE_PREFIX)) return PAGE_SET.has("/blog/*") ? "/blog/*" : null;
  return PAGE_SET.has(normalized) ? normalized : null;
}

/**
 * True only for a public marketing page the tracker may measure.
 *
 * @param {unknown} path
 * @returns {boolean}
 */
export function isEngagementPage(path) {
  return engagementPageBucket(path) !== null;
}
